/**
 * O QUE FOI DIGITADO NO PAINEL E AINDA NÃO FOI GRAVADO (pedido do dono,
 * 31/10/2026).
 *
 * O painel abre expandido, como janela, e por isso `Esc` e o clique fora o
 * FECHAM. Fechar não pode levar junto o que a pessoa digitou: com algum campo
 * alterado e não gravado, o painel pergunta antes ("Descartar o que foi
 * digitado?").
 *
 * Funciona para os 25 painéis sem que cada formulário avise nada. O painel
 * guarda o valor de cada controle NO MOMENTO EM QUE A PESSOA CHEGA NELE (foco,
 * toque ou tecla, o que vier primeiro) e, na hora de fechar, compara com o
 * valor de agora:
 *
 *  - o formulário que se limpa depois de gravar volta ao valor guardado, e
 *    não conta;
 *  - o formulário que some depois de gravar (a ficha volta à leitura) leva os
 *    controles junto, e controle fora da tela não conta;
 *  - GRAVAÇÃO ACEITA ESQUECE TUDO: depois de uma gravação que o servidor
 *    aceitou, o que está nos campos é o que vale, e a próxima tecla guarda o
 *    valor de novo. Sem isso, a alocação gravada (que deixa a obra nova
 *    escolhida no campo) pedia confirmação para fechar.
 *
 * A lógica é pura, sobre um leitor de valor injetado, para o teste não pedir
 * navegador. O leitor do DOM está em `valorDoControle`.
 */
export class AlteracoesNoPainel<C> {
  private readonly guardados = new Map<C, string>();

  constructor(
    private readonly ler: (controle: C) => string | null,
    private readonly naTela: (controle: C) => boolean,
  ) {}

  /** A pessoa chegou ao controle: guarda o valor dele, se ainda não guardou. */
  lembrar(controle: C): void {
    if (this.guardados.has(controle)) return;
    const valor = this.ler(controle);
    if (valor !== null) this.guardados.set(controle, valor);
  }

  /** Uma gravação foi aceita: o que está nos campos passa a ser o que vale. */
  esquecer(): void {
    this.guardados.clear();
  }

  /** Os controles ainda na tela com valor diferente do que tinham quando a pessoa chegou. */
  alterados(): readonly C[] {
    const lista: C[] = [];
    for (const [controle, antes] of this.guardados) {
      if (this.naTela(controle) && this.ler(controle) !== antes) lista.push(controle);
    }
    return lista;
  }

  temAlteracao(): boolean {
    return this.alterados().length > 0;
  }
}

/** Os controles que a pessoa preenche. O campo de escolha é o botão `combobox` desde 20/10/2026. */
export const SELETOR_DE_CONTROLE = 'input, textarea, select, button[role="combobox"]';

/** Tipos de `input` que não são digitação: botão, arquivo (tem fluxo próprio), busca. */
const NAO_E_DIGITACAO = new Set(['hidden', 'file', 'button', 'submit', 'reset', 'image', 'search']);

/**
 * O valor de um controle do DOM, como texto, ou `null` quando ele não conta:
 * desligado, só leitura, campo de busca, a busca DENTRO da lista de escolha
 * aberta (`data-camada-de-cima`) ou o que a tela marcou com
 * `data-nao-e-rascunho` (filtro que mora dentro do painel).
 */
export function valorDoControle(elemento: Element): string | null {
  if (elemento.closest('[data-camada-de-cima], [data-nao-e-rascunho]')) return null;
  if (elemento instanceof HTMLInputElement) {
    if (NAO_E_DIGITACAO.has(elemento.type) || elemento.disabled || elemento.readOnly) return null;
    if (elemento.type === 'checkbox' || elemento.type === 'radio') return elemento.checked ? 'marcado' : 'desmarcado';
    return elemento.value;
  }
  if (elemento instanceof HTMLTextAreaElement) {
    return elemento.disabled || elemento.readOnly ? null : elemento.value;
  }
  if (elemento instanceof HTMLSelectElement) {
    return elemento.disabled ? null : elemento.value;
  }
  if (elemento instanceof HTMLButtonElement && elemento.getAttribute('role') === 'combobox') {
    return elemento.disabled ? null : (elemento.textContent ?? '');
  }
  return null;
}
